import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Key, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const PW_INPUT_CLS = "rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-10 px-4 pr-10";

function PasswordField({ label, value, onChange, visible, onToggle, testid, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-zinc-700">{label}</Label>
      <div className="relative">
        <Input
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
          data-testid={testid}
          className={PW_INPUT_CLS}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-400 hover:text-[#001E44] transition-colors"
          aria-label={visible ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
        >
          {visible ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePasswordDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm: "" });
    setError("");
    setShow({ current: false, next: false, confirm: false });
  };

  const onOpen = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setOpen(false);
      reset();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#007BFF]/10 text-[#007BFF] flex items-center justify-center">
              <Key size={18} weight="bold" />
            </div>
            <div>
              <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
                Change password
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-0.5">
                Other sessions will be signed out after you save.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <PasswordField
            label="Current password"
            value={form.current_password}
            onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
            visible={show.current}
            onToggle={() => setShow((s) => ({ ...s, current: !s.current }))}
            testid="change-password-current"
            autoComplete="current-password"
          />
          <PasswordField
            label="New password"
            value={form.new_password}
            onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
            visible={show.next}
            onToggle={() => setShow((s) => ({ ...s, next: !s.next }))}
            testid="change-password-new"
            autoComplete="new-password"
          />
          <p className="text-[10px] text-zinc-400 -mt-2 pl-1">
            At least 8 characters. Mix in numbers and symbols for a stronger password.
          </p>
          <PasswordField
            label="Confirm new password"
            value={form.confirm}
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
            visible={show.confirm}
            onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
            testid="change-password-confirm"
            autoComplete="new-password"
          />

          {error && (
            <div
              className="rounded-xl bg-red-50 border border-red-100 text-red-700 text-xs font-medium px-3 py-2.5"
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpen(false)}
              className="rounded-full font-semibold text-sm h-10 px-5 text-zinc-500 hover:text-[#001E44]"
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full font-semibold text-sm h-10 px-5 shadow-sm"
              data-testid="change-password-save"
            >
              {busy ? "Saving..." : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
